import { useState, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsPlus, BsTrash, BsPencil, BsCheck2, BsXLg, BsGripVertical, BsEye, BsEyeSlash, BsLock } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';

import {
	createCustomFieldDef,
	updateCustomFieldDef,
	deleteCustomFieldDef,
	reorderFieldDefs,
} from '../../../api/crmApi';
import { useCustomFieldDefs, customFieldsKeys } from '../../../models/customFields/queries';

const ENTITY_TYPES = [
	{ value: 'company', label: 'Firma' },
	{ value: 'contact', label: 'Kontakt' },
	{ value: 'deal', label: 'Deal' },
	{ value: 'product', label: 'Produkt' },
];

const FIELD_TYPES = [
	{ value: 'text', label: 'Text (einzeilig)' },
	{ value: 'textarea', label: 'Text (mehrzeilig)' },
	{ value: 'number', label: 'Zahl' },
	{ value: 'date', label: 'Datum' },
	{ value: 'url', label: 'URL / Link' },
	{ value: 'select', label: 'Dropdown' },
	{ value: 'checkbox', label: 'Checkbox' },
];

const FieldSettingsRow = ({
	f,
	isEditing,
	editName,
	editOptions,
	onEditNameChange,
	onEditOptionsChange,
	onUpdate,
	onCancelEdit,
	onToggle,
	onStartEdit,
	onDelete,
}) => {
	if (isEditing) {
		return (
			<>
				<input
					type="text"
					value={editName}
					onChange={onEditNameChange}
					className="flex-1 text-sm border-b border-primary-300 bg-transparent outline-none py-0.5"
					autoFocus
					onKeyDown={(e) => {
						if (e.key === 'Enter') onUpdate();
						if (e.key === 'Escape') onCancelEdit();
					}}
				/>
				{f.fieldType === 'select' && (
					<input
						type="text"
						value={editOptions}
						onChange={onEditOptionsChange}
						placeholder="Optionen (kommagetrennt)"
						className="flex-1 text-xs border-b border-bgBase-500 bg-transparent outline-none py-0.5"
					/>
				)}
				<button onClick={onUpdate} className="text-success-700 hover:text-success-700 p-0.5">
					<BsCheck2 className="w-4 h-4" />
				</button>
				<button onClick={onCancelEdit} className="text-bgBase-800 hover:text-text p-0.5">
					<BsXLg className="w-3 h-3" />
				</button>
			</>
		);
	}

	return (
		<>
			<span
				className={`text-sm font-medium flex-1 truncate ${f.isVisible === false ? 'text-bgBase-800 line-through' : 'text-text'}`}
			>
				{f.name}
			</span>

			<span className="text-[10px] text-bgBase-800 bg-bgBase-400 rounded-full px-1.5 py-0.5 flex-shrink-0">
				{FIELD_TYPES.find((t) => t.value === f.fieldType)?.label ?? f.fieldType}
			</span>

			{f.isDefault && (
				<span className="text-[10px] text-primary-500 bg-primary-200 rounded-full px-1.5 py-0.5 flex-shrink-0">
					Standard
				</span>
			)}

			<button
				type="button"
				onClick={() => onToggle('isRequired')}
				className={`text-[10px] px-1.5 py-0.5 rounded-full transition-[filter] flex-shrink-0 ${
					f.isRequired
						? 'text-warning-700 bg-warning-200 hover:brightness-95'
						: 'text-bgBase-800 bg-bgBase-400 hover:brightness-95'
				}`}
				title={f.isRequired ? 'Pflichtfeld (klicken zum Entfernen)' : 'Optional (klicken für Pflichtfeld)'}
			>
				{f.isRequired ? 'Pflicht' : 'Optional'}
			</button>

			<button
				type="button"
				onClick={() => onToggle('isVisible')}
				className={`w-6 h-6 rounded-control flex items-center justify-center transition-colors flex-shrink-0 ${
					f.isVisible !== false
						? 'text-primary-600 hover:bg-primary-100'
						: 'text-bgBase-800 hover:bg-bgBase-400'
				}`}
				title={
					f.isVisible !== false
						? 'Sichtbar (klicken zum Ausblenden)'
						: 'Ausgeblendet (klicken zum Einblenden)'
				}
			>
				{f.isVisible !== false ? <BsEye className="w-3.5 h-3.5" /> : <BsEyeSlash className="w-3.5 h-3.5" />}
			</button>

			<IconButton icon={BsPencil} label="Umbenennen" onClick={onStartEdit} className="h-6 w-6" />

			{f.isDefault ? (
				<span
					className="text-bgBase-800 p-0.5 flex-shrink-0"
					title="Standard-Felder können nicht gelöscht werden"
				>
					<BsLock className="w-3 h-3" />
				</span>
			) : (
				<IconButton icon={BsTrash} label="Löschen" tone="danger" onClick={onDelete} className="h-6 w-6" />
			)}
		</>
	);
};

FieldSettingsRow.propTypes = {
	f: PropTypes.object.isRequired,
	isEditing: PropTypes.bool.isRequired,
	editName: PropTypes.string.isRequired,
	editOptions: PropTypes.string.isRequired,
	onEditNameChange: PropTypes.func.isRequired,
	onEditOptionsChange: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onCancelEdit: PropTypes.func.isRequired,
	onToggle: PropTypes.func.isRequired,
	onStartEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
};

const CustomFieldsSettingsTab = () => {
	const [activeEntity, setActiveEntity] = useState('company');
	const { data: rawFields = [], isLoading } = useCustomFieldDefs(activeEntity);
	const fields = [...rawFields].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
	const showError = useErrorStore((s) => s.showError);
	const qc = useQueryClient();

	// Add form state
	const [showAdd, setShowAdd] = useState(false);
	const [newName, setNewName] = useState('');
	const [newType, setNewType] = useState('text');
	const [newOptions, setNewOptions] = useState('');
	const [newRequired, setNewRequired] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	// Edit state
	const [editingId, setEditingId] = useState(null);
	const [editName, setEditName] = useState('');
	const [editOptions, setEditOptions] = useState('');
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleting, setDeleting] = useState(false);

	// Drag state
	const dragIdx = useRef(null);
	const [dragOverIdx, setDragOverIdx] = useState(null);

	const invalidate = () => qc.invalidateQueries({ queryKey: customFieldsKeys.all });

	// CRUD handlers

	const handleCreate = async () => {
		if (!newName.trim()) return;
		setSubmitting(true);
		try {
			await createCustomFieldDef({
				entityType: activeEntity,
				name: newName.trim(),
				fieldType: newType,
				selectOptions:
					newType === 'select'
						? newOptions
								.split(',')
								.map((s) => s.trim())
								.filter(Boolean)
						: null,
				isRequired: newRequired,
			});
			setShowAdd(false);
			setNewName('');
			setNewType('text');
			setNewOptions('');
			setNewRequired(false);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Feld konnte nicht erstellt werden' });
		} finally {
			setSubmitting(false);
		}
	};

	const handleUpdate = async (id) => {
		try {
			const patch = { name: editName.trim() };
			if (editOptions)
				patch.selectOptions = editOptions
					.split(',')
					.map((s) => s.trim())
					.filter(Boolean);
			await updateCustomFieldDef(id, patch);
			setEditingId(null);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Feld konnte nicht aktualisiert werden' });
		}
	};

	const handleToggle = async (field, key) => {
		try {
			await updateCustomFieldDef(field.id, { [key]: !field[key] });
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Feld konnte nicht aktualisiert werden' });
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		setDeleting(true);
		try {
			await deleteCustomFieldDef(deleteTarget.id);
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Feld konnte nicht gelöscht werden' });
		} finally {
			setDeleting(false);
			setDeleteTarget(null);
		}
	};

	const startEdit = (f) => {
		setEditingId(f.id);
		setEditName(f.name);
		setEditOptions(f.selectOptions?.join(', ') ?? '');
	};

	// Drag & drop reorder

	const handleDragStart = (idx) => {
		dragIdx.current = idx;
	};
	const handleDragOver = (e, idx) => {
		e.preventDefault();
		setDragOverIdx(idx);
	};
	const handleDragEnd = async () => {
		const from = dragIdx.current;
		const to = dragOverIdx;
		dragIdx.current = null;
		setDragOverIdx(null);

		if (from === null || to === null || from === to) return;

		const reordered = [...fields];
		const [moved] = reordered.splice(from, 1);
		reordered.splice(to, 0, moved);

		// Optimistic update
		const optimistic = reordered.map((f, i) => ({ ...f, sortOrder: i }));
		qc.setQueryData(customFieldsKeys.byEntity(activeEntity), optimistic);

		try {
			await reorderFieldDefs({
				entityType: activeEntity,
				fieldIds: reordered.map((f) => f.id),
			});
			invalidate();
		} catch (err) {
			showError({ error: err, title: 'Reihenfolge konnte nicht gespeichert werden' });
			invalidate(); // revert
		}
	};

	return (
		<div className="space-y-4">
			<div>
				<h2 className="text-sm font-semibold text-text">Felder</h2>
				<p className="text-xs text-bgBase-800">
					Konfiguriere Sichtbarkeit, Reihenfolge und Pflichtfelder für Firmen, Kontakte und Deals.
				</p>
			</div>

			<div className="flex gap-1 bg-bgBase-400 rounded-card p-0.5 w-fit">
				{ENTITY_TYPES.map((et) => (
					<button
						key={et.value}
						type="button"
						onClick={() => {
							setActiveEntity(et.value);
							setShowAdd(false);
							setEditingId(null);
						}}
						className={`text-xs px-3 py-1.5 rounded-control transition-colors ${
							activeEntity === et.value
								? 'bg-white text-primary-700 font-semibold'
								: 'text-text hover:text-text'
						}`}
					>
						{et.label}
					</button>
				))}
			</div>

			{isLoading && <p className="text-sm text-bgBase-800">Wird geladen …</p>}

			<div className="space-y-1">
				{fields.map((f, idx) => (
					<div
						key={f.id}
						draggable
						onDragStart={() => handleDragStart(idx)}
						onDragOver={(e) => handleDragOver(e, idx)}
						onDragEnd={handleDragEnd}
						className={`flex items-center gap-2.5 rounded-card border bg-white px-3 py-2 cursor-grab active:cursor-grabbing transition-colors ${
							dragOverIdx === idx ? 'border-primary-300 bg-primary-100' : 'border-bgBase-500'
						}`}
					>
						<BsGripVertical className="w-3 h-3 text-bgBase-800 flex-shrink-0" />
						<FieldSettingsRow
							f={f}
							isEditing={editingId === f.id}
							editName={editName}
							editOptions={editOptions}
							onEditNameChange={(e) => setEditName(e.target.value)}
							onEditOptionsChange={(e) => setEditOptions(e.target.value)}
							onUpdate={() => handleUpdate(f.id)}
							onCancelEdit={() => setEditingId(null)}
							onToggle={(key) => handleToggle(f, key)}
							onStartEdit={() => startEdit(f)}
							onDelete={() => setDeleteTarget(f)}
						/>
					</div>
				))}

				{!isLoading && fields.length === 0 && !showAdd && (
					<p className="text-sm text-bgBase-800 italic py-4 text-center">
						Keine Felder für {ENTITY_TYPES.find((t) => t.value === activeEntity)?.label}.
					</p>
				)}
			</div>

			{showAdd ? (
				<div className="rounded-card border border-primary-200 bg-primary-100 p-3 space-y-3">
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="block text-xs font-medium text-text mb-1">Name *</label>
							<TextInput
								value={newName}
								onChange={(e) => setNewName(e.target.value)}
								placeholder="z. B. Branche"
								autoFocus
							/>
						</div>
						<div>
							<label className="block text-xs font-medium text-text mb-1">Typ</label>
							<Select value={newType} onChange={(e) => setNewType(e.target.value)}>
								{FIELD_TYPES.map((ft) => (
									<option key={ft.value} value={ft.value}>
										{ft.label}
									</option>
								))}
							</Select>
						</div>
					</div>
					{newType === 'select' && (
						<div>
							<label className="block text-xs font-medium text-text mb-1">Optionen (kommagetrennt)</label>
							<TextInput
								value={newOptions}
								onChange={(e) => setNewOptions(e.target.value)}
								placeholder="Option A, Option B, Option C"
							/>
						</div>
					)}
					<Checkbox
						id="custom-field-required"
						checked={newRequired}
						onChange={setNewRequired}
						label="Pflichtfeld"
					/>
					<div className="flex justify-end gap-2">
						<Button variant="outline" size="sm" onClick={() => setShowAdd(false)}>
							Abbrechen
						</Button>
						<Button
							variant="solid"
							size="sm"
							onClick={handleCreate}
							loading={submitting}
							disabled={!newName.trim()}
						>
							Feld hinzufügen
						</Button>
					</div>
				</div>
			) : (
				<Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
					<BsPlus className="w-4 h-4 mr-1" />
					Eigenes Feld hinzufügen
				</Button>
			)}

			<ConfirmModal
				isOpen={Boolean(deleteTarget)}
				onClose={() => setDeleteTarget(null)}
				onConfirm={handleDelete}
				title="Feld löschen"
				message={
					<>
						Feld <strong>&quot;{deleteTarget?.name}&quot;</strong> wirklich löschen? Alle gespeicherten
						Werte gehen verloren.
					</>
				}
				isLoading={deleting}
			/>
		</div>
	);
};

export default CustomFieldsSettingsTab;
